import getClient from "./get-client";

const client = getClient();

export const MODULE_FLAGS = [
  { flag: "can_access_raw_query", label: "Diagnóstico", icon: "🩺" },
  { flag: "can_access_complicaciones", label: "Complicaciones", icon: "⚠" },
  { flag: "can_access_transcriptor", label: "Transcriptor", icon: "🎙" },
  { flag: "can_access_turno", label: "Turno", icon: "🛏" },
  { flag: "can_access_ingreso_epicrisis", label: "Informes", icon: "📝" },
  { flag: "can_access_epicrisis_radonc", label: "Epicrisis Radioterapia", icon: "🩻" },
  { flag: "can_access_finder", label: "Finder", icon: "🔎" },
  {
    flag: "can_access_indicaciones_ambulatorias",
    label: "Recetas Ambulatorias",
    icon: "💊",
  },
];

// #1416, R28/R29: el visualizador queda AFUERA de `MODULE_FLAGS` (la matriz de
// permisos de la pestaña Permisos) mientras `SHOW_VISUALIZER` sea `false`,
// pero sigue editable desde la sección "Otros módulos" del drawer, con el
// mismo PATCH y la misma auditoría que el resto de los flags — ninguna lógica
// de permisos cambia, solo dónde se muestra el switch.
export const OTHER_MODULE_FLAGS = [
  {
    flag: "can_access_visualizador_examenes",
    label: "Visualizador",
    icon: "🧪",
  },
];

// Permisos administrativos. Distintos de MODULE_FLAGS (accesos a la app).
// Solo puede tocarlos un superuser; el frontend igual los renderiza con
// switch para que un admin con can_manage_users vea el estado actual.
export const ADMIN_FLAGS = [
  {
    flag: "can_manage_users",
    label: "Gestión de usuarios",
    icon: "🛡",
    description:
      "Permite editar permisos de otros usuarios desde /admin/users (no incluye is_superuser).",
    superuserOnly: true,
  },
];

export const PROFESSION_LABELS = {
  DOCTOR: "Médico",
  INTERNO: "Interno",
  ESTUDIANTE: "Estudiante",
  KINESIOLOGIST: "Kinesiólogo",
  NURSE: "Enfermero",
  TENS: "TENS",
  OTHER: "Otro",
  NO_SALUD: "No es de la salud",
};

export const VERIFICATION_LABELS = {
  NOT_SUBMITTED: "No enviado",
  PENDING: "Pendiente",
  APPROVED: "Aprobado",
  REJECTED: "Rechazado",
};

export const listAdminUsers = (params = {}) =>
  client.get("/api/user/admin/users/", { params }).then((r) => r.data);

// Detalle de UN usuario (#1416, R14): trae `usage_14d`, que la lista NUNCA
// calcula (evita traer 14 días de eventos de todos los usuarios a la vez).
// Lo pide `UsageSummarySection` al abrir el drawer.
export const getAdminUser = (userId) =>
  client.get(`/api/user/admin/users/${userId}/`).then((r) => r.data);

export const updateAdminUser = (userId, patch) =>
  client.patch(`/api/user/admin/users/${userId}/`, patch).then((r) => r.data);

export const applyPresetToUser = (userId, profession) =>
  client
    .post(`/api/user/admin/users/${userId}/apply-preset/`, { profession })
    .then((r) => r.data);

// Aprueba la verificación profesional Y habilita al usuario en 1 llamada:
// verification_status=APPROVED + has_access + módulos por defecto de su
// profesión. Guard server-side (superuser / can_manage_users).
export const approveVerification = (userId) =>
  client
    .post(`/api/user/admin/users/${userId}/approve-verification/`)
    .then((r) => r.data);

// Rechaza la verificación Y corta `has_access`. El motivo es obligatorio
// (mínimo 3 caracteres, lo valida el backend): queda en `verification_notes` y
// en el audit log, y es lo que se lee después para saber por qué se rechazó.
export const rejectVerification = (userId, notes) =>
  client
    .post(`/api/user/admin/users/${userId}/reject-verification/`, { notes })
    .then((r) => r.data);

// Inventario de los documentos de verificación: qué subió, de qué tipo, cuánto
// pesa y de cuándo es. NO trae el contenido — cada archivo se baja aparte.
export const getUserDocuments = (userId) =>
  client.get(`/api/user/admin/users/${userId}/documents/`).then((r) => r.data);

// El archivo, como Blob. NO sirve un `<img src="/static/media/...">`: desde el
// 09-sep-2026 esa carpeta la tapa el nginx del proxy (servía la cédula de
// identidad de cualquier médico sin pedir sesión), y el endpoint que la
// reemplaza exige el token DRF, que un `<img>` no manda. Por eso se baja con
// este cliente y se pinta con un object URL — que el llamador tiene que
// liberar con `URL.revokeObjectURL` al cerrar.
export const fetchUserDocumentBlob = (userId, tipo) =>
  client
    .get(`/api/user/admin/users/${userId}/documents/${tipo}/`, {
      responseType: "blob",
    })
    .then((r) => ({
      blob: r.data,
      // El Content-Type manda sobre la extensión: es lo que el backend
      // decidió servir, y de él depende si esto se pinta como imagen o como
      // PDF embebido.
      contentType: String(
        r.data?.type || r.headers?.["content-type"] || ""
      ).split(";")[0],
    }));

export const revokeUserSessions = (userId) =>
  client
    .post(`/api/user/admin/users/${userId}/revoke-sessions/`)
    .then((r) => r.data);

// Pausa por inactividad (#1583, R23): el admin quita la pausa a mano. Responde la
// cuenta serializada, igual que el detalle.
export const quitarPausaUsuario = (userId) =>
  client.post(`/api/user/admin/users/${userId}/quitar-pausa/`).then((r) => r.data);

// Borrado definitivo (#1544). El backend exige `confirm_email` igual al email
// de la cuenta de esa URL, y si la cuenta arrastra trabajo clínico responde
// 409 con `clinico`/`quedan_huerfanas`: recién ahí se repite con force=true.
// Responde `{deleted: <snapshot>}`.
export const deleteAdminUser = (userId, { confirmEmail, force = false }) =>
  client
    .delete(`/api/user/admin/users/${userId}/`, {
      data: { confirm_email: confirmEmail, ...(force ? { force: true } : {}) },
    })
    .then((r) => r.data);

export const listAdminPresets = () =>
  client.get("/api/user/admin/presets/").then((r) => r.data);

export const getGlobalExamsUsage = (days = 30, granularity = "day") =>
  client
    .get("/api/user/admin/exams-usage/", { params: { days, granularity } })
    .then((r) => r.data);

// #1470 R16: ranking de formatos más elegidos por TODOS los médicos, PHI-free
// (nunca identifica a qué médico corresponde cada elección, solo cuántos son).
export const getPromptsRanking = () =>
  client.get("/api/user/admin/prompts-ranking/").then((r) => r.data);

// #1470 R19/R20: top de formatos de UN médico puntual, con `favorito`/
// `favorito_fijado` (mismos campos que el listado del propio médico, R4).
export const getUserPromptsTop = (userId) =>
  client.get(`/api/user/admin/users/${userId}/prompts-top/`).then((r) => r.data);

export const getUserAuditLog = (userId, limit = 50) =>
  client
    .get("/api/user/admin/audit-log/", {
      params: { user_id: userId, limit },
    })
    .then((r) => r.data);

// Prompts por unidad del Modo Turno (Drawer del usuario en /admin/users).
// GET trae el catálogo global + las unidades del usuario con sus keys activas;
// PATCH guarda una unidad. El ORDEN de prompt_keys es el orden del dropdown
// que ve el médico en el Turno, así que se manda tal cual.
// Pulido final #1470 (observación 4 del revisor): reusado tal cual, SOLO
// para leer `unidades[].name` desde `MedicoTopFormatosCard`, no agrega
// endpoint nuevo.
export const getUserTurnoPrompts = (userId) =>
  client.get(`/api/user/admin/users/${userId}/turno-prompts/`).then((r) => r.data);

// Suscripción y cobro (Planner #862, Entrega 1). `getSubscription` devuelve
// `null` cuando la cuenta no tiene fila (404 del backend), en vez de dejar
// que el 404 reviente el `.then` del llamador: el cajón necesita distinguir
// "sin suscripción todavía" de un error real.
export const getSubscription = (userId) =>
  client
    .get(`/api/billing/admin/users/${userId}/subscription/`)
    .then((r) => r.data)
    .catch((err) => {
      if (err?.response?.status === 404) return null;
      throw err;
    });

export const upsertSubscription = (userId, payload, { exists } = {}) =>
  client[exists ? "patch" : "post"](
    `/api/billing/admin/users/${userId}/subscription/`,
    payload
  ).then((r) => r.data);

export const getPayments = (userId) =>
  client.get(`/api/billing/admin/users/${userId}/payments/`).then((r) => r.data);

// Passthrough puro: `concepto` (R47, R55) y `monto_neto` (R51, cuando el
// llamador lo mande) viajan solos porque esta función no arma un allowlist
// propio, solo reenvía `payload`. No hace falta editar nada acá para que el
// concepto llegue al backend (Entrega 4).
export const registerPayment = (userId, payload) =>
  client
    .post(`/api/billing/admin/users/${userId}/payments/`, payload)
    .then((r) => r.data);

export const updateUnidadPrompts = (userId, unidadId, payload) =>
  client
    .patch(
      `/api/user/admin/users/${userId}/unidades/${unidadId}/prompts/`,
      payload
    )
    .then((r) => r.data);

// Novedades del panel (#1416, R16-R18, R24): `{days, items:[{kind, user_id,
// nombre, at, texto}]}`, ya ordenado y sin PHI — ver `admin_usage.activity_feed`.
export const getActivityFeed = (days = 7) =>
  client
    .get("/api/user/admin/users/activity-feed/", { params: { days } })
    .then((r) => r.data);

// Panorama "Cómo vamos" (#1679, R27): serie semanal `{hasta, semanas:[{lunes, cierre, totales, activos,
// etapas}], kpis, sin_registro}`. `semanas` = 12 o 26. Solo admin; metadatos agregados, sin datos de pacientes.
export const getPanorama = (semanas = 12) =>
  client.get("/api/user/admin/panorama/", { params: { semanas } }).then((r) => r.data);

// Siguiente paso por cuenta (#1679, R35): `{generado_at, pasos:{<user_id>:{tipo, titulo, detalle, fecha,
// precision, canal, recurso_n, recursos_m, funcion}}}`. Endpoint aparte y lento: se pide DESPUÉS de pintar la lista.
export const getSiguientesPasos = () =>
  client.get("/api/user/admin/users/siguiente-paso/").then((r) => r.data);

// Timeline de un usuario para "Ver timeline" del drawer (#1416, R47-R50):
// `{user_id, days, sessions:[...]}`, mismo motor que el timeline de staff
// (`finetuning.timeline.build_timeline`) más la clave `minutes` de R48.
export const getUserTimeline = (userId, days = 14) =>
  client
    .get(`/api/user/admin/users/${userId}/timeline/`, { params: { days } })
    .then((r) => r.data);

// Ficha ancha (#1741): la lectura completa en UNA respuesta (`base` = la ficha 360 del mes, `calendario` de dos meses, `sesiones`,
// `lectura`, `censo`, `fallos`). `mes` = "AAAA-MM" (opcional). Solo admin; sin PHI ni PII por construcción (R16).
// Cubre R1 — .claude/specs/1741-ficha-ancha-app/
export const getFichaAncha = (userId, mes) =>
  client
    .get(`/api/user/admin/users/${userId}/ficha-ancha/`, { params: mes ? { mes } : {} })
    .then((r) => r.data);

// Calificación manual (R14): `valor` = "ideal" | "potencial" | "no_calza" | "" (quitar). Responde `{icp}`.
export const calificarUsuario = (userId, valor) =>
  client.post(`/api/user/admin/users/${userId}/calificacion/`, { valor }).then((r) => r.data);

// "Marcar como contactada" (R17). Responde `{contacto}`.
// #1734 R43: desde Hoy el cuerpo lleva `{canal, carril}` (canal "whatsapp_personal" | "dm" | ...; carril de la
// cola) para que el toque quede como `contacto:<carril>:marcado:<id>`. Sin argumento va `{}` como siempre
// (la ficha 360 no cambia). Cubre R43 — .claude/specs/1734-admin-users-hoy/
export const marcarContactada = (userId, { canal, carril } = {}) =>
  client
    .post(`/api/user/admin/users/${userId}/contactada/`, {
      ...(canal ? { canal } : {}),
      ...(carril ? { carril } : {}),
    })
    .then((r) => r.data);

// Pestaña Hoy (#1734). Solo admin; nombre/correo/teléfono de médicos (no PHI). Cubre R40, R45, R48.
// `ahora/` es liviano (se sondea cada 60 s): `{generado_at, ventana_min, en_linea:[{user_id, aparato, modulo, desde,
// ultimo_evento_at, carril}]}`. `hoy/` trae los carriles, los indicadores y la foto del día: `{generado_at, carriles:
// {<carril>:[fila]}, en_cola, fuera_por_contacto, cubre_secuencia, indicadores}`. `pulso/` = calendario de 35 días y
// efecto de los contactos por carril. Contrato en .claude/specs/1734-admin-users-hoy/design.md.
export const getAhora = () => client.get("/api/user/admin/users/ahora/").then((r) => r.data);
export const getHoy = () => client.get("/api/user/admin/users/hoy/").then((r) => r.data);
export const getPulso = () => client.get("/api/user/admin/users/pulso/").then((r) => r.data);
// #1734 v5 (R77, R82): `conversaciones/` = tu último contacto y su último mensaje por cuenta (instantes y canal, sin nombres ni
// texto) y si tu WhatsApp personal está leído: `{generado_at, dias, wa_personal_leido, wa_personal_al_dia_hasta, cuentas:
// [{user_id, tuyo_at, tuyo_canal, suyo_at, suyo_canal}]}`. Alimenta la insignia "contactado hoy" y la zona de contactados.
export const getConversaciones = () => client.get("/api/user/admin/users/conversaciones/").then((r) => r.data);
// #1774: `dia/?fecha=AAAA-MM-DD` = el día de cada cuenta para la pestaña Hoy minimalista (tramos de sesión, minutos, documentos,
// acciones, «volvió», temperatura y `paises_cobrables`): `{fecha, es_hoy, generado_at, inicio_dia, fin_dia, cuentas, temperaturas,
// paises_cobrables}`. `dias/?mes=AAAA-MM` = los días del mes con ingresos para el calendario: `{mes, hoy, dias:[{fecha, n}]}`.
export const getHoyDia = (fecha) => client.get("/api/user/admin/users/dia/", { params: fecha ? { fecha } : {} }).then((r) => r.data);
export const getDiasConIngresos = (mes) => client.get("/api/user/admin/users/dias/", { params: { mes } }).then((r) => r.data);

// WhatsApp SmartDx (R44, R45): el bloque `whatsapp` suelto (para releerlo cada 15 s mientras hay una
// salida en curso) y la cola de salida. Encolar NO envía: deja la salida en `pendiente` y el Mini
// la consume. `payload` = {tipo:"texto", texto} | {tipo:"plantilla", plantilla}. Responde el bloque.
export const getWhatsappUsuario = (userId) =>
  client.get(`/api/user/admin/users/${userId}/whatsapp/`).then((r) => r.data);

export const encolarWhatsapp = (userId, payload) =>
  client.post(`/api/user/admin/users/${userId}/whatsapp/salida/`, payload).then((r) => r.data);

// Semáforo de censo por médico (#1555, R26 y R9). Solo admin; PHI-free por construcción
// (conteos y códigos cerrados, nunca el molde ni el nombre del médico).
// Resumen: `{ventana_dias, contadores, medicos, nunca}`.
export const getAdminCensoResumen = () =>
  client.get("/api/user/admin/censo/").then((r) => r.data);

// Detalle de un médico: `{user_id, ventana_dias, estado, pegados:[...]}`.
export const getAdminUserCenso = (userId) =>
  client.get(`/api/user/admin/users/${userId}/censo/`).then((r) => r.data);

// Correos por usuario (#1588 R21, R26, R27, R29). Solo admin; solo metadatos (claves, asuntos,
// fechas, estados y el error del SMTP truncado): nunca el cuerpo del correo ni `html_sha`.
// Checklist: `{generado_at, pagina, total, columnas:[{clave,nombre,regla}], filas:[{user_id,
// email, registro, celdas:[{clave,estado,fecha,precision,motivo,manual,n_envios}]}]}`.
export const getCorreosChecklist = ({ pagina = 1, q = "" } = {}) =>
  client
    .get("/api/user/admin/correos/checklist/", { params: { pagina, ...(q ? { q } : {}) } })
    .then((r) => r.data);

// Enviados y programados: `{generado_at, dias, enviados:[...], programados:[...]}` (dias 1..30).
export const getCorreosEnvios = (dias = 7) =>
  client.get("/api/user/admin/correos/envios/", { params: { dias } }).then((r) => r.data);
